import { usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"

// Outcome figures with the basis they rest on. BABCP private-practice guidance
// and the CAP/ASA code require published outcome claims to be evidenced, so
// Gisela::PagesController#evidenced_outcomes sends an empty object unless the
// "measure" field is filled in — this component simply agrees with that gate.
export type OutcomesContent = {
  heading?: string
  intro?: string
  stat1_value?: string
  stat1_label?: string
  stat2_value?: string
  stat2_label?: string
  stat3_value?: string
  stat3_label?: string
  measure?: string
  sample?: string
  caveat?: string
}

export function OutcomesBlock({ outcomes }: { outcomes: OutcomesContent }) {
  const t = usePage<PageProps>().props.translations.gisela_pages.about

  const stats = [
    { value: outcomes.stat1_value, label: outcomes.stat1_label },
    { value: outcomes.stat2_value, label: outcomes.stat2_label },
    { value: outcomes.stat3_value, label: outcomes.stat3_label },
  ].filter((s) => s.value)

  if (!outcomes.measure || stats.length === 0) return null

  return (
    <div className="rounded-2xl bg-white p-8 ring-1 ring-hairline sm:p-10">
      {outcomes.intro && <p className="mt-0 max-w-2xl text-ink-body">{outcomes.intro}</p>}

      <dl
        className={
          stats.length === 1
            ? "m-0 mt-8 grid grid-cols-1 gap-8"
            : stats.length === 2
              ? "m-0 mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2"
              : "m-0 mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3"
        }
      >
        {stats.map((stat) => (
          <div key={stat.label ?? stat.value}>
            <dt className="font-display text-5xl font-light text-teal">
              {stat.value}
            </dt>
            {stat.label && <dd className="m-0 mt-2 text-ink-body">{stat.label}</dd>}
          </div>
        ))}
      </dl>

      <div className="mt-10 border-t border-hairline pt-6 text-sm text-ink-muted">
        <p className="m-0">
          <span className="text-ink-display">{t.outcomes_measure_label}</span> {outcomes.measure}
        </p>
        {outcomes.sample && (
          <p className="mt-2 mb-0">
            <span className="text-ink-display">{t.outcomes_sample_label}</span> {outcomes.sample}
          </p>
        )}
        <p className="mt-2 mb-0">{outcomes.caveat || t.outcomes_caveat}</p>
      </div>
    </div>
  )
}
